import { useEffect, useState } from "react";

import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { WalletDebitsService } from "../services/walletDebits.service";
import type { WalletDebitSendPreview } from "../types/walletDebits.types";

export type WalletDebitSendPreviewState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; preview: WalletDebitSendPreview }
  | { status: "failed"; error: string };

interface Scope {
  organizationId: string;
  walletId: string;
  amount: string;
}

const keyOf = (scope: Scope | null) =>
  scope ? `${scope.organizationId}:${scope.walletId}:${scope.amount}` : null;

/**
 * What to send by hand for the amount being typed, once it stops changing. `scope` is null while there is no
 * valid amount to price, which reads idle.
 */
export function useWalletDebitSendPreview(scope: Scope | null): WalletDebitSendPreviewState {
  const key = useDebouncedValue(keyOf(scope));
  const [read, setRead] = useState<{ key: string; state: WalletDebitSendPreviewState } | null>(null);

  useEffect(() => {
    if (!key) return;
    const [organizationId, walletId, amount] = key.split(":");
    let cancelled = false;
    WalletDebitsService.sendPreview(organizationId, walletId, amount)
      .then((preview) => {
        if (!cancelled) setRead({ key, state: { status: "ready", preview } });
      })
      .catch((error: unknown) => {
        if (!cancelled)
          setRead({ key, state: { status: "failed", error: HttpApiService.getErrorMessage(error) } });
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  const current = keyOf(scope);
  if (!current) return { status: "idle" };
  if (current !== key || read?.key !== current) return { status: "loading" };
  return read.state;
}
